import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Card from "../components/Card";
import { db } from "../firebase/config";
import { collection, query, where, getDocs } from "firebase/firestore";

const Jobs = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilters, setSelectedFilters] = useState({
    jobTitle: "",
    jobType: "",
    englishLevel: "",
    hasTips: false,
    hasOvertime: false,
    transportation: false,
    housingAvailable: false,
  });

  const usersMap = {};

  // Sabit pozisyon listesi
  const jobPositions = [
    "Server",
    "Busser",
    "Food Runner",
    "Hostess",
    "Kitchen Help",
    "Dishwasher",
    "Sales Assistant",
    "Photographer",
    "Lifeguard",
    "Other",
  ];

  // Firestore'dan iş ilanlarını çek
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        // Önce tüm kullanıcıları çek
        const usersRef = collection(db, "users");
        const usersSnapshot = await getDocs(usersRef);
        usersSnapshot.forEach((doc) => {
          const userData = doc.data();
          usersMap[userData.uid] = userData;
        });

        // Sonra iş ilanlarını çek
        const jobsRef = collection(db, "listings");
        const q = query(jobsRef, where("listingType", "==", "job"));
        const jobsSnapshot = await getDocs(q);
        const jobsList = jobsSnapshot.docs.map((doc) => {
          const jobData = doc.data();
          const username = usersMap[jobData.userId]?.username;
          const capitalizedUsername = username
            ? username.charAt(0).toUpperCase() + username.slice(1)
            : "Username Not Available";
          return {
            id: doc.id,
            ...jobData,
            companyName: capitalizedUsername,
          };
        });
        setJobs(jobsList);

        setError("");
      } catch (err) {
        console.error("Error fetching jobs:", err);
        setError("Failed to load jobs. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, [usersMap]);

  // Filtreleme fonksiyonu
  const filteredJobs = jobs.filter((job) => {
    const matchesSearch =
      job.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.companyName?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilters =
      (!selectedFilters.jobTitle || job.title === selectedFilters.jobTitle) &&
      (!selectedFilters.jobType || job.jobType === selectedFilters.jobType) &&
      (!selectedFilters.englishLevel ||
        job.englishLevel === selectedFilters.englishLevel) &&
      (!selectedFilters.hasTips || job.tipsIncluded === true) &&
      (!selectedFilters.hasOvertime || job.overtimeAvailable === true) &&
      (!selectedFilters.transportation ||
        job.transportationProvided === true) &&
      (!selectedFilters.housingAvailable || job.housingProvided === true);

    return matchesSearch && matchesFilters;
  });

  // Filtreleri sıfırla
  const clearFilters = () => {
    setSearchTerm("");
    setSelectedFilters({
      jobTitle: "",
      jobType: "",
      englishLevel: "",
      hasTips: false,
      hasOvertime: false,
      transportation: false,
      housingAvailable: false,
    });
  };

  if (loading) {
    return (
      <div className='min-h-screen bg-[#F8F9FA] p-4 flex items-center justify-center'>
        <div className='text-xl font-architects-daughter text-gray-600'>
          Loading...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='min-h-screen bg-[#F8F9FA] p-4 flex items-center justify-center'>
        <div className='text-xl font-architects-daughter text-red-600'>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-[#F8F9FA] p-4'>
      {/* Tabs */}
      <div className='max-w-7xl mx-auto mb-8'>
        <div className='flex gap-0'>
          <button
            onClick={() => navigate("/jobs")}
            className={`w-1/2 py-4 text-xl font-architects-daughter border-2 border-blue-600 rounded-l hover:bg-blue-50 cursor-pointer transition-colors
                  ${location.pathname === "/jobs" ? "bg-blue-100" : ""}`}>
            Jobs
          </button>
          <button
            onClick={() => navigate("/housing")}
            className={`w-1/2 py-4 text-xl font-architects-daughter border-2 border-l-0 border-blue-600 rounded-r hover:bg-blue-50 cursor-pointer transition-colors
                  ${location.pathname === "/housing" ? "bg-blue-100" : ""}`}>
            Housing
          </button>
        </div>
      </div>

      {/* Ana İçerik */}
      <div className='max-w-7xl mx-auto px-4 flex flex-col md:flex-row'>
        {/* Filtreler - Desktop */}
        <div className='hidden md:block w-56 mr-8'>
          <div className='sticky top-4'>
            <div className='flex justify-between items-center mb-4'>
              <h2 className='text-xl font-architects-daughter'>Filters</h2>
              <button
                onClick={clearFilters}
                className='text-blue-600 text-sm font-architects-daughter hover:underline'>
                Clear All
              </button>
            </div>

            {/* Job Title Filter */}
            <div className='mb-6'>
              <h3 className='font-architects-daughter mb-2'>Position</h3>
              <div className='space-y-2'>
                {jobPositions.map((title) => (
                  <label key={title} className='flex items-center'>
                    <input
                      type='radio'
                      name='jobTitle'
                      checked={selectedFilters.jobTitle === title}
                      onChange={() =>
                        setSelectedFilters((prev) => ({
                          ...prev,
                          jobTitle: title,
                        }))
                      }
                      className='mr-2'
                    />
                    <span className='font-architects-daughter text-sm'>
                      {title}
                    </span>
                  </label>
                ))}
                <label className='flex items-center'>
                  <input
                    type='radio'
                    name='jobTitle'
                    checked={selectedFilters.jobTitle === ""}
                    onChange={() =>
                      setSelectedFilters((prev) => ({ ...prev, jobTitle: "" }))
                    }
                    className='mr-2'
                  />
                  <span className='font-architects-daughter text-sm'>
                    All Positions
                  </span>
                </label>
              </div>
            </div>

            {/* Job Type Filter */}
            <div className='mb-6'>
              <h3 className='font-architects-daughter mb-2'>Job Type</h3>
              <div className='space-y-2'>
                {["full-time", "part-time", "weekend", "flexible"].map(
                  (type) => (
                    <label key={type} className='flex items-center'>
                      <input
                        type='radio'
                        name='jobType'
                        checked={selectedFilters.jobType === type}
                        onChange={() =>
                          setSelectedFilters((prev) => ({
                            ...prev,
                            jobType: type,
                          }))
                        }
                        className='mr-2'
                      />
                      <span className='font-architects-daughter text-sm capitalize'>
                        {type.replace("-", " ")}
                      </span>
                    </label>
                  )
                )}
                <label className='flex items-center'>
                  <input
                    type='radio'
                    name='jobType'
                    checked={selectedFilters.jobType === ""}
                    onChange={() =>
                      setSelectedFilters((prev) => ({ ...prev, jobType: "" }))
                    }
                    className='mr-2'
                  />
                  <span className='font-architects-daughter text-sm'>
                    All Types
                  </span>
                </label>
              </div>
            </div>

            {/* Additional Filters */}
            <div className='mb-6'>
              <h3 className='font-architects-daughter mb-2'>
                Additional Filters
              </h3>
              <div className='space-y-2'>
                <label className='flex items-center'>
                  <input
                    type='checkbox'
                    checked={selectedFilters.hasTips}
                    onChange={(e) =>
                      setSelectedFilters((prev) => ({
                        ...prev,
                        hasTips: e.target.checked,
                      }))
                    }
                    className='mr-2'
                  />
                  <span className='font-architects-daughter text-sm'>
                    Tips Included
                  </span>
                </label>
                <label className='flex items-center'>
                  <input
                    type='checkbox'
                    checked={selectedFilters.hasOvertime}
                    onChange={(e) =>
                      setSelectedFilters((prev) => ({
                        ...prev,
                        hasOvertime: e.target.checked,
                      }))
                    }
                    className='mr-2'
                  />
                  <span className='font-architects-daughter text-sm'>
                    Overtime Available
                  </span>
                </label>
                <label className='flex items-center'>
                  <input
                    type='checkbox'
                    checked={selectedFilters.transportation}
                    onChange={(e) =>
                      setSelectedFilters((prev) => ({
                        ...prev,
                        transportation: e.target.checked,
                      }))
                    }
                    className='mr-2'
                  />
                  <span className='font-architects-daughter text-sm'>
                    Transportation Provided
                  </span>
                </label>
                <label className='flex items-center'>
                  <input
                    type='checkbox'
                    checked={selectedFilters.housingAvailable}
                    onChange={(e) =>
                      setSelectedFilters((prev) => ({
                        ...prev,
                        housingAvailable: e.target.checked,
                      }))
                    }
                    className='mr-2'
                  />
                  <span className='font-architects-daughter text-sm'>
                    Housing Provided
                  </span>
                </label>
              </div>
            </div>

            {/* English Level Filter */}
            <div className='mb-6'>
              <h3 className='font-architects-daughter mb-2'>English Level</h3>
              <div className='space-y-2'>
                {["basic", "intermediate", "advanced", "fluent"].map(
                  (level) => (
                    <label key={level} className='flex items-center'>
                      <input
                        type='radio'
                        name='englishLevel'
                        checked={selectedFilters.englishLevel === level}
                        onChange={() =>
                          setSelectedFilters((prev) => ({
                            ...prev,
                            englishLevel: level,
                          }))
                        }
                        className='mr-2'
                      />
                      <span className='font-architects-daughter text-sm capitalize'>
                        {level}
                      </span>
                    </label>
                  )
                )}
                <label className='flex items-center'>
                  <input
                    type='radio'
                    name='englishLevel'
                    checked={selectedFilters.englishLevel === ""}
                    onChange={() =>
                      setSelectedFilters((prev) => ({
                        ...prev,
                        englishLevel: "",
                      }))
                    }
                    className='mr-2'
                  />
                  <span className='font-architects-daughter text-sm'>
                    Any Level
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Ana İçerik */}
        <div className='flex-1'>
          {/* Arama Kutusu */}
          <div className='mb-6'>
            <div className='relative'>
              <input
                type='text'
                placeholder='Search jobs by title, company, or location...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className='w-full p-3 border-2 border-gray-200 rounded-lg font-architects-daughter focus:outline-none focus:border-blue-500'
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600'>
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Mobil Filtreler */}
          <div className='md:hidden mb-6 space-y-4'>
            <div className='grid grid-cols-2 gap-4'>
              {/* Job Title Filter - Mobile */}
              <select
                value={selectedFilters.jobTitle}
                onChange={(e) =>
                  setSelectedFilters((prev) => ({
                    ...prev,
                    jobTitle: e.target.value,
                  }))
                }
                className='p-2 border rounded focus:outline-none focus:border-blue-500'>
                <option value=''>All Positions</option>
                {jobPositions.map((title) => (
                  <option key={title} value={title}>
                    {title}
                  </option>
                ))}
              </select>

              <select
                value={selectedFilters.jobType}
                onChange={(e) =>
                  setSelectedFilters((prev) => ({
                    ...prev,
                    jobType: e.target.value,
                  }))
                }
                className='p-2 border rounded focus:outline-none focus:border-blue-500'>
                <option value=''>All Job Types</option>
                <option value='full-time'>Full Time</option>
                <option value='part-time'>Part Time</option>
                <option value='weekend'>Weekend Only</option>
                <option value='flexible'>Flexible</option>
              </select>
            </div>

            <div className='grid grid-cols-2 gap-4'>
              <label className='flex items-center space-x-2 bg-white p-2 rounded border'>
                <input
                  type='checkbox'
                  checked={selectedFilters.hasTips}
                  onChange={(e) =>
                    setSelectedFilters((prev) => ({
                      ...prev,
                      hasTips: e.target.checked,
                    }))
                  }
                />
                <span>Tips Included</span>
              </label>

              <label className='flex items-center space-x-2 bg-white p-2 rounded border'>
                <input
                  type='checkbox'
                  checked={selectedFilters.hasOvertime}
                  onChange={(e) =>
                    setSelectedFilters((prev) => ({
                      ...prev,
                      hasOvertime: e.target.checked,
                    }))
                  }
                />
                <span>Overtime Available</span>
              </label>

              <label className='flex items-center space-x-2 bg-white p-2 rounded border'>
                <input
                  type='checkbox'
                  checked={selectedFilters.transportation}
                  onChange={(e) =>
                    setSelectedFilters((prev) => ({
                      ...prev,
                      transportation: e.target.checked,
                    }))
                  }
                />
                <span>Transportation Provided</span>
              </label>

              <label className='flex items-center space-x-2 bg-white p-2 rounded border'>
                <input
                  type='checkbox'
                  checked={selectedFilters.housingAvailable}
                  onChange={(e) =>
                    setSelectedFilters((prev) => ({
                      ...prev,
                      housingAvailable: e.target.checked,
                    }))
                  }
                />
                <span>Housing Provided</span>
              </label>
            </div>
          </div>

          {/* İlanların Listesi */}
          <div className='mb-4 text-gray-600 font-architects-daughter'>
            {filteredJobs.length} job{filteredJobs.length !== 1 ? "s" : ""}{" "}
            found
          </div>

          <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
            {filteredJobs.map((job) => (
              <Card
                key={job.id}
                id={job.id}
                type='jobs'
                title={job.title}
                subtitle={job.location}
                tags={[
                  job.jobType,
                  `$${job.salary}/hr`,
                  ...(job.hasTips ? ["+tips"] : []),
                  ...(job.hasOvertime ? ["+overtime"] : []),
                  ...(job.housingAvailable ? ["housing av."] : []),
                  ...(job.transportation ? ["transportation av."] : []),
                ]}
                date={new Date(
                  job.createdAt.seconds * 1000
                ).toLocaleDateString()}
                companyName={job.companyName}
                detailsButtonPosition='bottom-right'
              />
            ))}
          </div>

          {filteredJobs.length === 0 && (
            <div className='text-center py-12'>
              <p className='text-gray-600 font-architects-daughter'>
                No jobs found matching your criteria
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Jobs;
